import { getAuthToken, getStoredUser, getAuthHeaders } from '@/utils/auth';
import { clearAuth } from '@/utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Users, TrendingUp, DollarSign, Building2, LogOut, BarChart3, Briefcase, Key
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
import Nav from '@/components/Nav';
import PartnerCRM from '@/components/PartnerCRM';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Luxury Real Estate / Broker Background Images
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1768069794857-9306ac167c6e?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1758448656990-398443036eb4?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1758194190679-198a77cba84f?auto=format&fit=crop&w=1920&q=80'
];

const BrokerAgentDashboard = () => {
  const navigate = useNavigate();
  const { formatPrice: formatCurrency } = useCurrency();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('crm');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  const handleLogout = useCallback(() => {
    clearAuth();
    navigate('/partner/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/partner/crm/analytics`, {
        headers: getAuthHeaders()
      });
      setAnalytics(res.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const userData = JSON.stringify(getStoredUser());
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchData();
    
    // Rotate background images
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const isBroker = user?.role === 'broker';
  const portalName = isBroker ? 'Broker Portal' : 'Agent Portal';
  const accentColor = isBroker ? 'purple' : 'blue';

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <Nav variant="partner" partnerLabel={portalName} companyName={user?.company_name || user?.name} />

      {/* Hero Section with Luxury Background */}
      <div className="relative h-[320px] overflow-hidden" data-testid="broker-hero">
        {HERO_IMAGES.map((img, index) => (
          <motion.div
            key={img}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${img})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentBgIndex ? 1 : 0 }}
            transition={{ duration: 1.5 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-obsidian" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        
        <div className="relative z-10 max-w-[1440px] mx-auto px-8 h-full flex items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl ${isBroker ? 'bg-purple-500/20' : 'bg-blue-500/20'} backdrop-blur-sm flex items-center justify-center`}>
                <Key className={`w-6 h-6 ${isBroker ? 'text-purple-400' : 'text-blue-400'}`} />
              </div>
              <span className={`${isBroker ? 'text-purple-400/80' : 'text-blue-400/80'} text-sm uppercase tracking-widest`}>
                {isBroker ? 'Real Estate Broker' : 'Property Agent'}
              </span>
            </div>
            <h1 className="font-playfair text-5xl font-semibold mb-3 text-white">
              {isBroker ? 'Broker Dashboard' : 'Agent Dashboard'}
            </h1>
            <p className="text-white/70 text-lg">Welcome back, <span className="text-gold">{user?.name}</span></p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-8 py-8 -mt-16 relative z-20">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="broker-stats">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
            <Users className="w-10 h-10 text-gold mb-4 relative z-10" />
            <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.leads?.total || 0}</div>
            <div className="text-text-secondary">Total Leads</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-emerald-500/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all" />
            <TrendingUp className="w-10 h-10 text-emerald-400 mb-4 relative z-10" />
            <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.conversion_rate || 0}%</div>
            <div className="text-text-secondary">Conversion Rate</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
            <DollarSign className="w-10 h-10 text-gold mb-4 relative z-10" />
            <div className="text-3xl font-bold mb-1 relative z-10">{formatCurrency(analytics?.total_revenue)}</div>
            <div className="text-text-secondary">Total Commission</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} 
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-${isBroker ? 'purple' : 'blue'}-500/30 transition-all duration-300`}>
            <div className={`absolute top-0 right-0 w-32 h-32 bg-${isBroker ? 'purple' : 'blue'}-500/10 rounded-full blur-3xl group-hover:bg-${isBroker ? 'purple' : 'blue'}-500/20 transition-all`} />
            <Briefcase className={`w-10 h-10 ${isBroker ? 'text-purple-400' : 'text-blue-400'} mb-4 relative z-10`} />
            <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.leads?.closed_won || 0}</div>
            <div className="text-text-secondary">Closed Deals</div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white/5 backdrop-blur-sm rounded-xl p-2" data-testid="broker-tabs">
          {['crm', 'deals', 'reports'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
              className={`px-6 py-3 rounded-lg transition-all duration-300 capitalize font-medium ${
                activeTab === tab 
                  ? `bg-gradient-to-r ${isBroker ? 'from-purple-500 to-purple-600' : 'from-blue-500 to-blue-600'} text-white shadow-lg ${isBroker ? 'shadow-purple-500/25' : 'shadow-blue-500/25'}` 
                  : 'text-text-secondary hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab === 'crm' ? 'Lead Management' : tab}
            </button>
          ))}
        </div>

        {/* CRM Tab */}
        {activeTab === 'crm' && <PartnerCRM partnerId={user?.id} />}

        {/* Deals Tab */}
        {activeTab === 'deals' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="glass-card p-6">
                <h4 className="text-text-secondary text-sm mb-2">Active Negotiations</h4>
                <div className="text-3xl font-bold text-gold">{analytics?.leads?.negotiation || 0}</div>
              </div>
              <div className="glass-card p-6">
                <h4 className="text-text-secondary text-sm mb-2">Interested Clients</h4>
                <div className="text-3xl font-bold text-purple-400">{analytics?.leads?.interested || 0}</div>
              </div>
              <div className="glass-card p-6">
                <h4 className="text-text-secondary text-sm mb-2">New Leads</h4>
                <div className="text-3xl font-bold text-blue-400">{analytics?.leads?.new || 0}</div>
              </div>
            </div>
            <div className="glass-card p-8 text-center">
              <Building2 className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
              <h3 className="font-playfair text-xl font-semibold mb-2">Deal Pipeline</h3>
              <p className="text-text-secondary">View and manage your deals in the Lead Management tab above.</p>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="glass-card p-8 text-center">
            <BarChart3 className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
            <h3 className="font-playfair text-2xl font-semibold mb-2">Reports Coming Soon</h3>
            <p className="text-text-secondary">Commission reports and performance analytics will be available soon.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrokerAgentDashboard;
